import React, { useState } from 'react';
import { DatePicker, DefaultButton, Dropdown, IDropdownOption, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import moment from 'moment';
import { useStore } from '../../../../stores/root';
import { IOperationItem } from '../types';
import Label from '../../../typography/label';
import Text from '../../../typography/text';
import GeneralService from '../../../../services/general';
import ErrorService from '../../../../services/general/error';
import ShipSchedulesService from '../../../../services/data/ships/shipSchedules';
import OrderTrackingService from '../../../../services/orders/trackings/orderTracking';
import { faCheck, faCircleCheck, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface IQuickStatusModalProps {
    item: IOperationItem;
    isOpen: boolean;
    onDismiss(refresh?: boolean): void;
}

const SHIP_STATUS_OPTIONS: IDropdownOption[] = [
    { key: 'sailing', text: '🚢 Sedang Berlayar (Sailing / Departed)' },
    { key: 'arrived', text: '⚓ Sudah Sandar / Tiba di Pelabuhan (Arrived)' },
    { key: 'completed', text: '✅ Selesai (Completed)' },
];

const TRACKING_STATUS_OPTIONS: IDropdownOption[] = [
    { key: 'in_progress', text: '⏳ Sedang Dijalankan (In Progress)' },
    { key: 'completed', text: '✅ Selesai Dikerjakan (Completed)' },
];

const QuickStatusModal: React.FC<IQuickStatusModalProps> = ({ item, isOpen, onDismiss }) => {
    const { banner } = useStore();
    const [submitting, setSubmitting] = useState<boolean>(false);
    const isShip = item.itemType === 'ship';

    const defaultStatus = isShip 
        ? (item.status === 'scheduled' ? 'sailing' : 'arrived')
        : (item.status === 'scheduled' ? 'in_progress' : 'completed');

    const [status, setStatus] = useState<string>(defaultStatus);
    const [actualDate, setActualDate] = useState<Date>(new Date());
    const [notes, setNotes] = useState<string>('');

    const handleSubmit = async () => {
        try {
            setSubmitting(true);
            const dateIso = moment(actualDate).toISOString();

            if (isShip) {
                const fd = new FormData();
                fd.append('status', status);
                if (status === 'sailing') {
                    fd.append('actualDeparture', dateIso);
                } else if (status === 'arrived' || status === 'completed') {
                    fd.append('actualArrival', dateIso);
                }

                await ShipSchedulesService.update(item.rawId, fd);
            } else if (item.orderId) {
                const fd = new FormData();
                fd.append('status', status);

                if (item.activityType === 'loading' || item.activityType === 'unloading') {
                    if (status === 'in_progress') {
                        fd.append('actualStartDate', dateIso);
                    } else if (status === 'completed') {
                        fd.append('actualEndDate', dateIso);
                    }
                } else if (item.activityType === 'dooring') {
                    if (status === 'in_progress') {
                        fd.append('actualDepartureDate', dateIso);
                    } else if (status === 'completed') {
                        fd.append('actualArrivalDate', dateIso);
                    }
                }

                if (notes) {
                    fd.append('notes', notes);
                }

                await OrderTrackingService.update(item.orderId, item.rawId, fd);
            }

            banner.add({
                key: 'update_status_success',
                variant: 'success',
                icon: faCheck,
                text: `Status berhasil diperbarui menjadi ${status.toUpperCase()} untuk ${item.shipName || item.orderNumber || ''}`
            });

            onDismiss(true);
        } catch (e) {
            banner.add({
                key: 'update_status_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: 'Gagal memperbarui status. Error: ' + ErrorService.getMessage(e)
            });
            setSubmitting(false);
        }
    };

    return (
        <Panel
            headerText={`Update Operational Status`}
            isOpen={isOpen}
            type={PanelType.medium}
            onDismiss={() => onDismiss(false)}
            isFooterAtBottom={true}
            onRenderFooterContent={() => (
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    {!submitting ? (
                        <>
                            <PrimaryButton
                                text="Konfirmasi & Update Status"
                                iconProps={{ iconName: 'CheckMark' }}
                                onClick={handleSubmit}
                            />
                            <DefaultButton text="Batal" onClick={() => onDismiss(false)} />
                        </>
                    ) : (
                        <Spinner size={SpinnerSize.medium} label="Memperbarui status..." labelPosition="right" />
                    )}
                </Stack>
            )}
        >
            <Stack tokens={{ childrenGap: 18 }} styles={{ root: { marginTop: 15 } }}>
                {/* Summary Info */}
                <Stack
                    styles={{
                        root: {
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            padding: '14px 16px',
                        }
                    }}
                    tokens={{ childrenGap: 8 }}
                >
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                        <span
                            style={{
                                background: item.activityColor + '20',
                                color: item.activityColor,
                                border: `1px solid ${item.activityColor}40`,
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 700,
                            }}
                        >
                            <FontAwesomeIcon icon={item.activityIcon} style={{ marginRight: 5 }} />
                            {item.activityLabel}
                        </span>
                        <span
                            style={{
                                background: '#e2e8f0',
                                color: '#475569',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 600,
                            }}
                        >
                            Status Saat Ini: {item.statusLabel}
                        </span>
                    </Stack>

                    <Text size="large" style={{ fontWeight: 600, color: '#0f172a' }}>
                        {item.shipName ? `${item.shipName} ${item.voy ? `(Voy: ${item.voy})` : ''}` : item.orderNumber || '-'}
                    </Text>

                    {item.locationText && (
                        <Text size="small" style={{ color: '#64748b' }}>
                            📍 {item.locationText}
                        </Text>
                    )}
                </Stack>

                {/* Status Input */}
                <Stack tokens={{ childrenGap: 14 }}>
                    <Dropdown
                        label="Pilih Status Baru *"
                        options={isShip ? SHIP_STATUS_OPTIONS : TRACKING_STATUS_OPTIONS}
                        selectedKey={status}
                        onChange={(_, opt) => {
                            if (opt) setStatus(opt.key as string);
                        }}
                        required
                    />

                    <DatePicker
                        label="Waktu / Tanggal Eksekusi Aktual *"
                        value={actualDate}
                        onSelectDate={(date) => {
                            if (date) setActualDate(date);
                        }}
                        formatDate={GeneralService.formatDate}
                        isRequired
                    />

                    <TextField
                        label="Catatan Lapangan (Opsional)"
                        multiline
                        rows={3}
                        value={notes}
                        onChange={(_, val) => setNotes(val || '')}
                        placeholder="Contoh: Kapal sandar pukul 08:30 WITA di Dermaga Berlian..."
                    />
                </Stack>
            </Stack>
        </Panel>
    );
};

export default QuickStatusModal;
